import { MODE } from '@/env'
import { useLocalStorage } from '@/utils/useLocalStorage'
import { useOnWindowResize } from '@/utils/useOnWindowResize'
import { PropsWithChildren, createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react'
import { TooltipProvider } from '../components/Tooltip'
import { LOCAL_STORAGE_KEY_IS_THEME_DARK } from '../config'
import { Theme, darkTheme, lightTheme } from '../theme'
import '../theme.css'

export function ifPrefersColorSchemeDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)')?.matches || true
}

document.addEventListener('DOMContentLoaded', (): void => {
  const isThemeDark = window.localStorage.getItem(LOCAL_STORAGE_KEY_IS_THEME_DARK)
  const isDark = (isThemeDark ?? ifPrefersColorSchemeDark()).toString()

  document.documentElement.className = isDark === 'true' ? 'dark' : ''
})

const accentColor = (() => {
  switch (MODE) {
    case 'development':
      return 'rgba(245, 53, 57, 1)'
    case 'staging':
      return 'rgba(0, 127, 245, 1)'
    case 'production-non-store':
      return 'rgba(10, 208, 0, 1)'
    case 'production':
    default:
      return '#FF9735'
  }
})()

type UiKitProviderValue = {
  theme: Theme
  isThemeDark: boolean
  setIsThemeDark: (isDark: ((isDark: boolean | undefined) => boolean) | boolean) => void
  /** @deprecated */
  isMobile: boolean
}

const UiKitProviderContext = createContext({} as UiKitProviderValue)

export function ThemeProvider({ children }: PropsWithChildren): React.JSX.Element {
  const [isMobile, setIsMobile] = useState(false)
  const [isThemeDarkLocalStorage, setIsThemeDarkLocalStorage] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEY_IS_THEME_DARK,
    ifPrefersColorSchemeDark()
  )
  //TODO use one variable across the whole app
  useOnWindowResize(() => setIsMobile(window.innerWidth <= 899))

  useLayoutEffect(() => {
    document.documentElement.style.setProperty('--logo-color', accentColor)
  }, [])

  const setIsThemeDark = useCallback(
    (isDark: ((isDark: boolean | undefined) => boolean) | boolean) => {
      setIsThemeDarkLocalStorage(isDark)
      document.documentElement.className = isDark ? 'dark' : ''
    },
    [setIsThemeDarkLocalStorage]
  )

  const value: UiKitProviderValue = useMemo(
    () => ({
      theme: isThemeDarkLocalStorage ? darkTheme : lightTheme,
      isThemeDark: isThemeDarkLocalStorage,
      setIsThemeDark,
      isMobile
    }),
    [isThemeDarkLocalStorage, setIsThemeDark, isMobile]
  )

  return (
    <UiKitProviderContext.Provider value={value}>
      <TooltipProvider>{children}</TooltipProvider>
    </UiKitProviderContext.Provider>
  )
}

export function useUiKit(): UiKitProviderValue {
  return useContext(UiKitProviderContext)
}
